import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { DropDownFilterSettings } from "@progress/kendo-angular-dropdowns";
import { KeyValueObject } from "../../shared/models/core/KeyValueObject";
import { LookupService } from "../../shared/services/lookup.service";
import { forkJoin, of, Subject } from "rxjs";
import { ReportsService } from "../../shared/services/reports.service";
import { RequestsReportModel } from "../../shared/models/reports/requests-report.model";
import { NotificationService } from "../../core/services/notification.service";
import { AppService } from "../../app.service";
import { NotificationType } from "../../core/services/notification-type";
import { saveAs } from 'file-saver';
import { takeUntil, tap } from "rxjs/operators";

@Component({
    selector: 'requests-report',
    templateUrl: './requests-report.component.html',
    styleUrls: [
        './requests-report.component.scss',
        '../../../vendor/libs/ngx-perfect-scrollbar/ngx-perfect-scrollbar.scss',
        '../../../vendor/libs/angular2-ladda/angular2-ladda.scss'
    ]
})
export class RequestsReportComponent implements OnInit, OnDestroy {

    public dropDownFilterSettings: DropDownFilterSettings = { caseSensitive: false, operator: 'startsWith' };

    @Input() withActivities: boolean = false;
    @Input() reportFileNamePrefix: string = "Заявки";

    private _destroy$ = new Subject<boolean>();

    finalActivityStatusKindId: number = 1;
    notFinalActivityStatusKindId: number = 0;

    users: KeyValueObject[] = [];
    requestCategories: KeyValueObject[] = [];
    requestTypes: KeyValueObject[] = [];
    initTypes: KeyValueObject[] = [];
    contragentProjects: KeyValueObject[] = [];
    activityStatusKinds: KeyValueObject[] = [];

    dataGenerating: boolean = false;

    model: RequestsReportModel = new RequestsReportModel();

    constructor(
        private lookupService: LookupService,
        private reportsService: ReportsService,
        private notificationService: NotificationService
    ) { }

    ngOnInit(): void {
        const users$ = this.lookupService.getData("users", null);
        const requestCategories$ = this.lookupService.getData("request-categories", null);
        const requestTypes$ = this.lookupService.getData("request-types", null);
        const initTypes$ = this.withActivities
            ? this.lookupService.getDataForCombobox("init-types", null)
            : of([]);
        const contragentProjects$ = this.withActivities
            ? this.lookupService.getData("contragent-projects", null)
            : of([]);

        forkJoin([users$, requestCategories$, requestTypes$, initTypes$, contragentProjects$])
        .subscribe(([users, requestCategories, requestTypes, initTypes, contragentProjects]) => {
            this.users = users;
            this.requestCategories = requestCategories;
            this.requestTypes = requestTypes;
            this.initTypes = initTypes;
            this.contragentProjects = contragentProjects;
            this.contragentProjects.unshift({id: 0, name: "(Не указан)"} as KeyValueObject);
            this.activityStatusKinds = [
                new KeyValueObject(null, 'Все'),
                new KeyValueObject(this.finalActivityStatusKindId, "Завершенные"),
                new KeyValueObject(this.notFinalActivityStatusKindId, "Не завершенные")
            ];

            this.model = new RequestsReportModel();
            this.model.withActivities = this.withActivities;
        });
    }

    public ngOnDestroy() {
		this._destroy$.next(true);
		this._destroy$.complete();
	}

    generateReport() {
        if (this.model.activityStatusKindId != null) {
            this.model.isFinalActivities = this.model.activityStatusKindId == this.finalActivityStatusKindId;
        }
        else {
            this.model.isFinalActivities = null;
        }

        this.dataGenerating = true;
        const action = this.withActivities
            ? this.reportsService.getRequestsWithActivitiesReport(this.model)
            : this.reportsService.getRequestsWithoutActivitiesReport(this.model);

        action
        .pipe(tap(() => takeUntil(this._destroy$)))
        .subscribe(blob => {
            if (blob.type === 'application/octet-stream') {
                let nowDate = new Date();
                saveAs(blob, `${this.reportFileNamePrefix}_${nowDate.toLocaleDateString()}_${nowDate.getHours()}_${nowDate.getMinutes()}_${nowDate.getSeconds()}.xlsx`);
                this.dataGenerating = false;
            } else if (blob.type === 'application/json') {
                blob
                    .text()
                    .then(result => {
                        const response = JSON.parse(result);
                        if (!response.isSuccessful) {
                            this.notificationService.error({
                                title: 'Ошибка',
                                message: response.errorDescription as string,
                                notificationType: NotificationType.SweetAlert
                            });
                        }

                        this.dataGenerating = false;
                    });
            }
        });
    }
    
    get canGenerateReport() {
        return this.model.startDate != null && this.model.endDate != null && this.model.startDate <= this.model.endDate;
    }
}